import { createElement, useState } from 'react';

import type { BlockRecord } from '@/lib/blocks/types';
import type { BlockPropertyRecord, DatabaseColumnDefinition } from '@/lib/databases/types';
import {
  buildMonthGrid,
  monthLabel,
  rowsForMonth,
  shiftMonth,
} from '@/lib/databases/calendar-grid';
import { placedItemColorClass, rowOptionColor } from '@/lib/databases/row-color';

type DatabaseCalendarProps = {
  rows: BlockRecord[];
  propertiesByRowId: Record<string, BlockPropertyRecord[]>;
  calendarColumnId: string;
  colorColumn?: DatabaseColumnDefinition;
  onOpenRow?: (rowId: string) => void;
  onPeekRow?: (rowId: string) => void;
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function stopBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function itemClassName(
  rowId: string,
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
  colorColumn: DatabaseColumnDefinition | undefined,
): string {
  return [
    'cs-db-calendar-item',
    placedItemColorClass(rowOptionColor(propertiesByRowId[rowId], colorColumn)),
  ]
    .filter(Boolean)
    .join(' ');
}

export function DatabaseCalendar({
  rows,
  propertiesByRowId,
  calendarColumnId,
  colorColumn,
  onOpenRow,
  onPeekRow,
}: DatabaseCalendarProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  function goMonth(delta: number) {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  }

  const { byDay, undated } = rowsForMonth(rows, propertiesByRowId, calendarColumnId, year, month);
  const cells = buildMonthGrid(year, month);

  return createElement(
    'div',
    { className: 'cs-db-calendar', onMouseDown: stopBubble, onKeyDownCapture: stopBubble },
    createElement(
      'div',
      { className: 'cs-db-calendar-header' },
      createElement(
        'button',
        { type: 'button', className: 'cs-db-calendar-nav', onClick: () => goMonth(-1) },
        '‹',
      ),
      createElement('span', { className: 'cs-db-calendar-title' }, monthLabel(year, month)),
      createElement(
        'button',
        { type: 'button', className: 'cs-db-calendar-nav', onClick: () => goMonth(1) },
        '›',
      ),
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-calendar-today',
          onClick: () => {
            const t = new Date();
            setYear(t.getFullYear());
            setMonth(t.getMonth());
          },
        },
        'Today',
      ),
    ),
    createElement(
      'div',
      { className: 'cs-db-calendar-weekdays' },
      ...WEEKDAYS.map((d) => createElement('div', { key: d, className: 'cs-db-calendar-weekday' }, d)),
    ),
    createElement(
      'div',
      { className: 'cs-db-calendar-grid' },
      ...cells.map((cell, index) => {
        if (cell.day === null || !cell.iso) {
          return createElement('div', {
            key: `pad-${index}`,
            className: 'cs-db-calendar-day cs-db-calendar-day-pad',
          });
        }
        const dayRows = byDay.get(cell.day) ?? [];
        return createElement(
          'div',
          { key: cell.iso, className: 'cs-db-calendar-day' },
          createElement('div', { className: 'cs-db-calendar-day-num' }, String(cell.day)),
          createElement(
            'div',
            { className: 'cs-db-calendar-day-items' },
            ...dayRows.map((row) =>
              createElement(
                'button',
                {
                  key: row.id,
                  type: 'button',
                  className: itemClassName(row.id, propertiesByRowId, colorColumn),
                  onClick: () => (onPeekRow ?? onOpenRow)?.(row.id),
                  onDoubleClick: onPeekRow && onOpenRow
                    ? () => onOpenRow(row.id)
                    : undefined,
                  onMouseDown: stopBubble,
                },
                row.title?.trim() || 'Untitled',
              ),
            ),
          ),
        );
      }),
    ),
    undated.length > 0
      ? createElement(
          'div',
          { className: 'cs-db-calendar-undated' },
          createElement('div', { className: 'cs-db-calendar-undated-label' }, 'No date'),
          ...undated.map((row) =>
            createElement(
              'button',
              {
                key: row.id,
                type: 'button',
                className: itemClassName(row.id, propertiesByRowId, colorColumn),
                onClick: () => (onPeekRow ?? onOpenRow)?.(row.id),
                onDoubleClick: onPeekRow && onOpenRow
                  ? () => onOpenRow(row.id)
                  : undefined,
              },
              row.title?.trim() || 'Untitled',
            ),
          ),
        )
      : null,
  );
}
